import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Clapperboard, Wrench, Building2, Check, ArrowRight, Loader2 } from "lucide-react";
import { SOCIAL_PLATFORMS } from "@/lib/socials";
import HaloWordmark from "@/components/HaloWordmark";
import { JoinHeadshotUpload, JoinFileUpload } from "@/components/JoinUploads";

const SECTION_ICONS = { cast: Clapperboard, crew: Wrench, company: Building2 };
const SECTION_COPY = {
  cast: { title: "Cast / performer", blurb: "Acting, presenting, voice, dance, stunts" },
  crew: { title: "Crew / production", blurb: "Camera, sound, edit, design, production" },
  company: { title: "Company / vendor", blurb: "You run or represent a business" },
};

const list = (v) => v.split(",").map((s) => s.trim()).filter(Boolean);

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-white/70">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-white/40">{hint}</p>}
  </div>
);

const Group = ({ title, children }) => (
  <section className="space-y-4">
    <div className="flex items-center gap-3">
      <h2 className="text-base font-medium">{title}</h2>
      <div className="h-px flex-1 bg-white/10" />
    </div>
    {children}
  </section>
);

export default function JoinForm() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [f, setF] = useState({
    name: "", preferred_name: "", pronouns: "", email: "", phone: "", location: "",
    website: "", primary_role: "", secondary_roles: "", skills: "", languages: "",
    date_of_birth: "", height_cm: "", hair_colour: "", eye_colour: "", ethnicity: "",
    measurements: "", unions: "", agent_name: "", agent_email: "", agent_phone: "",
    agent_company: "", currency: "AUD", day_rate: "", half_day_rate: "", weekly_rate: "",
    travel_willingness: "", availability_notes: "", equipment_owned: "",
    has_own_transport: false, has_own_equipment: false, has_public_liability: false,
    showreel_url: "", resume_url: "", portfolio_urls: "", photo_url: "", credits_text: "",
    notes: "", socials: {}, honeypot: "",
    company: { name: "", type: "", abn: "", website: "", address: "", services: "", team_size: "", established_year: "", notes: "" },
  });

  useEffect(() => {
    api.get(`/public/intake/${token}`)
      .then(({ data }) => setMeta(data))
      .catch(() => setError("This sign-up link isn't active any more. Ask whoever shared it for a new one."));
  }, [token]);

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
  const setCompany = (patch) => setF((prev) => ({ ...prev, company: { ...prev.company, ...patch } }));
  const toggleSection = (key) =>
    setSections((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));
  const has = (key) => sections.includes(key);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...f,
        sections,
        secondary_roles: list(f.secondary_roles),
        skills: list(f.skills),
        languages: list(f.languages),
        unions: list(f.unions),
        equipment_owned: list(f.equipment_owned),
        portfolio_urls: list(f.portfolio_urls),
        height_cm: f.height_cm ? parseFloat(f.height_cm) : null,
        day_rate: f.day_rate ? parseFloat(f.day_rate) : null,
        half_day_rate: f.half_day_rate ? parseFloat(f.half_day_rate) : null,
        weekly_rate: f.weekly_rate ? parseFloat(f.weekly_rate) : null,
        company: has("company") && f.company.name
          ? { ...f.company, services: list(f.company.services) } : null,
      };
      const { data } = await api.post(`/public/intake/${token}`, body);
      setSent(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong sending that — try again.");
    } finally { setSaving(false); }
  };

  if (error && !meta) {
    return (
      <div className="min-h-screen bg-[#151C2B] text-white grid place-items-center px-6">
        <div className="max-w-md text-center space-y-3">
          <HaloWordmark tone="dark" height={18} />
          <p className="text-white/70" data-testid="join-error">{error}</p>
        </div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="min-h-screen bg-[#151C2B] text-white grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-[#151C2B] text-white px-5 py-16 md:py-24" data-testid="join-thanks">
        <div className="mx-auto max-w-xl text-center space-y-6">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[hsl(var(--mahano-jade)/0.18)] text-[hsl(var(--mahano-jade))]">
            <Check className="h-7 w-7" />
          </div>
          <div className="space-y-3">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">You're on the list</h1>
            <p className="text-white/60">
              {sent.message || `Thanks — ${meta.business_name} has your details.`} Someone will be in
              touch when a project lines up.
            </p>
          </div>
          <Card className="bg-white/[0.04] border-white/10 p-6 text-left space-y-3">
            <div className="flex items-center gap-2 text-sm text-white/50">
              <HaloWordmark tone="dark" height={18} />
            </div>
            <p className="text-sm text-white/70">
              <span className="font-medium text-white">
                {meta.business_name?.trim().toLowerCase() === "halo"
                  ? "Studios like this run their day on Mahano OS"
                  : `${meta.business_name} runs their day on Mahano OS`}
              </span> —
              the white-label operating system for studios, producers and freelancers. Contacts,
              projects, invoices, scheduling and campaigns in one place, under your own brand.
            </p>
            <Link to="/welcome">
              <Button variant="secondary" data-testid="join-halo-cta">
                See what Mahano OS does <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  const inputClass = "bg-white/[0.04] border-white/10 text-white placeholder:text-white/30";

  return (
    <div className="min-h-screen bg-[#151C2B] text-white">
      <div className="mx-auto max-w-3xl px-5 py-12 md:py-20">
        <header className="space-y-5">
          {meta.logo_url
            ? <img src={meta.logo_url} alt={meta.business_name} className="h-10 w-auto" />
            : meta.is_default_brand
              ? <HaloWordmark tone="dark" height={24} data-testid="join-lockup" />
              : <div className="text-sm uppercase tracking-[0.2em] text-white/40">{meta.business_name}</div>}
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            {meta.headline}
          </h1>
          <p className="text-white/60 md:text-lg max-w-xl">{meta.intro}</p>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--mahano-jade))]"
             data-testid="join-tagline">{meta.tagline || "Life and work, in harmony"}</p>
        </header>

        <form className="mt-12 space-y-12" onSubmit={submit}>
          <Group title="What do you do?">
            <div className="grid gap-3 sm:grid-cols-3">
              {Object.keys(SECTION_COPY).map((key) => {
                const Icon = SECTION_ICONS[key];
                const active = has(key);
                return (
                  <button type="button" key={key} onClick={() => toggleSection(key)}
                          data-testid={`join-section-${key}`}
                          className={`text-left rounded-xl border p-4 transition-all ${active ? "border-[hsl(var(--mahano-jade)/0.6)] bg-[hsl(var(--mahano-jade)/0.08)]" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
                    <Icon className={`h-5 w-5 ${active ? "text-[hsl(var(--mahano-jade))]" : "text-white/40"}`} />
                    <div className="mt-3 text-sm font-medium">{SECTION_COPY[key].title}</div>
                    <div className="text-[11px] text-white/45 mt-0.5">{SECTION_COPY[key].blurb}</div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-white/40">Tick everything that applies — most people wear a few hats.</p>
          </Group>

          <Group title="About you">
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="Full name *">
                <Input required data-testid="join-name" className={inputClass} value={f.name}
                       onChange={(e) => set({ name: e.target.value })} />
              </F>
              <F label="Goes by">
                <Input className={inputClass} value={f.preferred_name}
                       onChange={(e) => set({ preferred_name: e.target.value })} />
              </F>
              <F label="Email">
                <Input type="email" data-testid="join-email" className={inputClass} value={f.email}
                       onChange={(e) => set({ email: e.target.value })} />
              </F>
              <F label="Phone">
                <Input data-testid="join-phone" className={inputClass} value={f.phone}
                       onChange={(e) => set({ phone: e.target.value })} />
              </F>
              <F label="Where you're based">
                <Input className={inputClass} value={f.location} placeholder="City, state"
                       onChange={(e) => set({ location: e.target.value })} />
              </F>
              <F label="Pronouns">
                <Input className={inputClass} value={f.pronouns}
                       onChange={(e) => set({ pronouns: e.target.value })} />
              </F>
              <F label="Website">
                <Input className={inputClass} value={f.website} placeholder="https://"
                       onChange={(e) => set({ website: e.target.value })} />
              </F>
              <F label="Languages" hint="Comma separated">
                <Input className={inputClass} value={f.languages}
                       onChange={(e) => set({ languages: e.target.value })} />
              </F>
            </div>
          </Group>

          {(has("cast") || has("crew")) && (
            <Group title={has("crew") ? "Your work" : "Your craft"}>
              <div className="grid gap-4 sm:grid-cols-2">
                <F label={has("cast") && !has("crew") ? "Main discipline" : "Main role"}
                   hint="e.g. Actor, Gaffer, Editor, Producer">
                  <Input data-testid="join-primary-role" className={inputClass} value={f.primary_role}
                         onChange={(e) => set({ primary_role: e.target.value })} />
                </F>
                <F label="Other roles you cover" hint="Comma separated">
                  <Input className={inputClass} value={f.secondary_roles}
                         onChange={(e) => set({ secondary_roles: e.target.value })} />
                </F>
                <F label="Skills" hint="Comma separated">
                  <Input className={inputClass} value={f.skills}
                         onChange={(e) => set({ skills: e.target.value })} />
                </F>
                <F label="Unions / memberships" hint="Comma separated">
                  <Input className={inputClass} value={f.unions}
                         onChange={(e) => set({ unions: e.target.value })} />
                </F>
              </div>
              <F label={has("cast") ? "Credits — one per line" : "Recent work — one per line"}>
                <Textarea rows={4} data-testid="join-credits" className={inputClass} value={f.credits_text}
                          placeholder={"The Long Road — Lead (2025)\nSummer Ad Campaign — 1st AC (2024)"}
                          onChange={(e) => set({ credits_text: e.target.value })} />
              </F>
            </Group>
          )}

          {has("cast") && (
            <Group title="Casting details">
              <div className="grid gap-4 sm:grid-cols-3">
                <F label="Height (cm)">
                  <Input type="number" className={inputClass} value={f.height_cm}
                         onChange={(e) => set({ height_cm: e.target.value })} />
                </F>
                <F label="Hair colour">
                  <Input className={inputClass} value={f.hair_colour}
                         onChange={(e) => set({ hair_colour: e.target.value })} />
                </F>
                <F label="Eye colour">
                  <Input className={inputClass} value={f.eye_colour}
                         onChange={(e) => set({ eye_colour: e.target.value })} />
                </F>
                <F label="Date of birth">
                  <Input type="date" className={inputClass} value={f.date_of_birth}
                         onChange={(e) => set({ date_of_birth: e.target.value })} />
                </F>
                <F label="Ethnicity / casting bracket">
                  <Input className={inputClass} value={f.ethnicity}
                         onChange={(e) => set({ ethnicity: e.target.value })} />
                </F>
                <F label="Measurements">
                  <Input className={inputClass} value={f.measurements}
                         onChange={(e) => set({ measurements: e.target.value })} />
                </F>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <F label="Agent / manager">
                  <Input className={inputClass} value={f.agent_name}
                         onChange={(e) => set({ agent_name: e.target.value })} />
                </F>
                <F label="Agency">
                  <Input className={inputClass} value={f.agent_company}
                         onChange={(e) => set({ agent_company: e.target.value })} />
                </F>
                <F label="Agent email">
                  <Input className={inputClass} value={f.agent_email}
                         onChange={(e) => set({ agent_email: e.target.value })} />
                </F>
                <F label="Agent phone">
                  <Input className={inputClass} value={f.agent_phone}
                         onChange={(e) => set({ agent_phone: e.target.value })} />
                </F>
              </div>
            </Group>
          )}

          {(has("cast") || has("crew")) && (
            <Group title="Rates & availability">
              <div className="grid gap-4 sm:grid-cols-4">
                <F label="Currency">
                  <Input className={inputClass} value={f.currency}
                         onChange={(e) => set({ currency: e.target.value })} />
                </F>
                <F label="Day rate">
                  <Input type="number" data-testid="join-day-rate" className={inputClass} value={f.day_rate}
                         onChange={(e) => set({ day_rate: e.target.value })} />
                </F>
                <F label="Half day">
                  <Input type="number" className={inputClass} value={f.half_day_rate}
                         onChange={(e) => set({ half_day_rate: e.target.value })} />
                </F>
                <F label="Weekly">
                  <Input type="number" className={inputClass} value={f.weekly_rate}
                         onChange={(e) => set({ weekly_rate: e.target.value })} />
                </F>
              </div>
              <F label="Availability notes">
                <Textarea rows={2} className={inputClass} value={f.availability_notes}
                          placeholder="Weekends only, away in July, etc."
                          onChange={(e) => set({ availability_notes: e.target.value })} />
              </F>
              <div className="grid gap-4 sm:grid-cols-2">
                <F label="How far you'll travel" hint="local · regional · national · international">
                  <Input className={inputClass} value={f.travel_willingness}
                         onChange={(e) => set({ travel_willingness: e.target.value })} />
                </F>
                <F label="Equipment you own" hint="Comma separated">
                  <Input className={inputClass} value={f.equipment_owned}
                         onChange={(e) => set({ equipment_owned: e.target.value })} />
                </F>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                {[["has_own_transport", "I have my own transport"],
                  ["has_own_equipment", "I have my own equipment"],
                  ["has_public_liability", "I hold public liability insurance"]].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs cursor-pointer">
                    <Checkbox data-testid={`join-${key}`} checked={f[key]}
                              onCheckedChange={(v) => set({ [key]: !!v })} />
                    {label}
                  </label>
                ))}
              </div>
            </Group>
          )}

          {has("company") && (
            <Group title="Your company">
              <div className="grid gap-4 sm:grid-cols-2">
                <F label="Company name *">
                  <Input data-testid="join-company-name" className={inputClass} value={f.company.name}
                         required onChange={(e) => setCompany({ name: e.target.value })} />
                </F>
                <F label="What kind of company" hint="e.g. production company, supplier, agency">
                  <Input className={inputClass} value={f.company.type}
                         onChange={(e) => setCompany({ type: e.target.value })} />
                </F>
                <F label="Website">
                  <Input className={inputClass} value={f.company.website}
                         onChange={(e) => setCompany({ website: e.target.value })} />
                </F>
                <F label="ABN / registration">
                  <Input className={inputClass} value={f.company.abn}
                         onChange={(e) => setCompany({ abn: e.target.value })} />
                </F>
                <F label="Address">
                  <Input className={inputClass} value={f.company.address}
                         onChange={(e) => setCompany({ address: e.target.value })} />
                </F>
                <F label="Services you offer" hint="Comma separated">
                  <Input className={inputClass} value={f.company.services}
                         onChange={(e) => setCompany({ services: e.target.value })} />
                </F>
                <F label="Team size">
                  <Input className={inputClass} value={f.company.team_size} placeholder="1-10"
                         onChange={(e) => setCompany({ team_size: e.target.value })} />
                </F>
                <F label="Established">
                  <Input className={inputClass} value={f.company.established_year} placeholder="2019"
                         onChange={(e) => setCompany({ established_year: e.target.value })} />
                </F>
              </div>
            </Group>
          )}

          <Group title="Links & socials">
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="Showreel">
                <Input className={inputClass} value={f.showreel_url} placeholder="https://vimeo.com/…"
                       onChange={(e) => set({ showreel_url: e.target.value })} />
              </F>
              <F label="Resume / CV link" hint="Or upload the file below">
                <Input className={inputClass} value={f.resume_url} placeholder="https://"
                       onChange={(e) => set({ resume_url: e.target.value })} />
              </F>
              <F label="Other portfolio links" hint="Comma separated">
                <Input className={inputClass} value={f.portfolio_urls}
                       onChange={(e) => set({ portfolio_urls: e.target.value })} />
              </F>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <JoinHeadshotUpload token={token} value={f.photo_url}
                                  onChange={(v) => set({ photo_url: v })} />
              <JoinFileUpload token={token} value={f.resume_url}
                              onChange={(v) => set({ resume_url: v })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {SOCIAL_PLATFORMS.map(({ key, label, placeholder, icon: Icon }) => (
                <div key={key} className="flex items-center gap-2">
                  <Icon className="h-3.5 w-3.5 shrink-0 text-white/35" />
                  <Input className={inputClass} data-testid={`join-social-${key}`}
                         placeholder={`${label} — ${placeholder}`} value={f.socials[key] || ""}
                         onChange={(e) => set({ socials: { ...f.socials, [key]: e.target.value } })} />
                </div>
              ))}
            </div>
          </Group>

          <Group title="Anything else">
            <F label="Tell us whatever else matters">
              <Textarea rows={4} data-testid="join-notes" className={inputClass} value={f.notes}
                        onChange={(e) => set({ notes: e.target.value })} />
            </F>
          </Group>

          {/* honeypot — hidden from people, catnip for bots */}
          <input type="text" name="company_website" tabIndex={-1} autoComplete="off"
                 value={f.honeypot} onChange={(e) => set({ honeypot: e.target.value })}
                 className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" />

          {error && <p className="text-sm text-[hsl(8_60%_68%)]" data-testid="join-submit-error">{error}</p>}

          <div className="flex flex-col sm:flex-row items-center gap-4 pb-8">
            <Button type="submit" size="lg" data-testid="join-submit" disabled={saving || !f.name.trim()}
                    className="w-full sm:w-auto">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                      : <>Join the hub <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
            <p className="text-[11px] text-white/40">
              Your details go straight to {meta.business_name} — nobody else.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
